import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calendar, CheckCircle2, ExternalLink, RefreshCw, Unlink } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function Integrations() {
  const { apiCall } = useAuth();
  const [googleStatus, setGoogleStatus] = useState({ configured: false, connected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    try {
      const r = await apiCall("get", "/integrations/google/status");
      setGoogleStatus(r.data);
    } catch {
      toast.error("Erro ao verificar status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Check if returning from OAuth
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      handleOAuthCallback(code);
    }
  }, []);

  const handleOAuthCallback = async (code) => {
    setConnecting(true);
    try {
      const res = await apiCall("post", "/integrations/google/callback", { code });
      toast.success(`Google Calendar conectado: ${res.data.email}`);
      window.history.replaceState({}, document.title, "/integracoes");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao conectar");
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await apiCall("get", "/integrations/google/authorize");
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Configure as credenciais Google primeiro");
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Desconectar Google Calendar?")) return;
    try {
      await apiCall("delete", "/integrations/google/disconnect");
      toast.success("Desconectado");
      load();
    } catch {
      toast.error("Erro ao desconectar");
    }
  };

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="integrations-page">
      <PageHeader
        overline="Configurações"
        title="Integrações"
        subtitle="Conecte serviços externos para centralizar sua administração"
      />

      <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="google-calendar-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div
            className="h-12 w-12 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: "#EEF2FF" }}
          >
            <Calendar className="h-6 w-6" strokeWidth={1.5} style={{ color: "#4285F4" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-medium font-heading">Google Calendar</h3>
              {googleStatus.connected && (
                <span className="text-xs uppercase tracking-widest font-medium px-2 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#D1FAE5", color: "#166534" }}>
                  <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
                  Conectado
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Sincronize agendamentos, cobranças e lembretes diretamente no seu Google Calendar. Centraliza tudo em um só lugar.
            </p>
            {googleStatus.connected && googleStatus.email && (
              <p className="text-xs text-muted-foreground mt-2">
                Conta conectada: <span className="font-medium text-foreground">{googleStatus.email}</span>
              </p>
            )}

            <div className="mt-4 flex gap-2 flex-wrap">
              {loading ? (
                <span className="text-sm text-muted-foreground">Verificando...</span>
              ) : !googleStatus.configured ? (
                <div className="text-sm p-3 rounded-md w-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                  ⚠️ Integração não configurada. O administrador precisa adicionar <code>GOOGLE_CLIENT_ID</code> e <code>GOOGLE_CLIENT_SECRET</code> nas variáveis de ambiente.
                </div>
              ) : googleStatus.connected ? (
                <>
                  <Button variant="outline" onClick={handleConnect} disabled={connecting} data-testid="google-reconnect-button">
                    <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Reconectar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDisconnect}
                    className="text-destructive hover:text-destructive"
                    data-testid="google-disconnect-button"
                  >
                    <Unlink className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Desconectar
                  </Button>
                </>
              ) : (
                <Button onClick={handleConnect} disabled={connecting} data-testid="google-connect-button">
                  {connecting ? "Conectando..." : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Conectar com Google
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {googleStatus.connected && (
          <div className="mt-5 pt-5 border-t border-border">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-heading">
              Como Usar
            </div>
            <ul className="text-sm space-y-2 text-muted-foreground">
              <li className="flex gap-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} style={{ color: "#166534" }} />
                <span>Em qualquer agendamento, clique no ícone do Google Calendar para sincronizar</span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} style={{ color: "#166534" }} />
                <span>Lembretes de clientes podem ser enviados ao calendário para alertas no celular</span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} style={{ color: "#166534" }} />
                <span>Alertas de 1h e 24h antes do horário direto no Google</span>
              </li>
            </ul>
          </div>
        )}
      </Card>

      <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="whatsapp-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#D1FAE5" }}>
            <ExternalLink className="h-6 w-6" strokeWidth={1.5} style={{ color: "#25D366" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-medium font-heading">WhatsApp Click-to-Chat</h3>
              <span className="text-xs uppercase tracking-widest font-medium px-2 py-0.5 rounded inline-flex items-center gap-1" style={{ background: "#D1FAE5", color: "#166534" }}>
                <CheckCircle2 className="h-3 w-3" strokeWidth={2} />
                Ativo
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Já ativo! Em Cobranças e no Dashboard, clique no ícone do WhatsApp para abrir uma conversa com a mensagem pronta.
            </p>
          </div>
        </div>
      </Card>

      {/* Facebook — placeholder até integração oficial da Meta */}
      <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="facebook-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#DBEAFE" }}>
            <ExternalLink className="h-6 w-6" strokeWidth={1.5} style={{ color: "#1877F2" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-medium font-heading">Facebook</h3>
              <span className="text-xs uppercase tracking-widest font-medium px-2 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#6B7280" }}>
                Integração ainda não configurada
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              A conexão será feita via login oficial da Meta (Graph API + Facebook Login). Nenhuma
              simulação: nada aparece nesta tela até o fluxo estar implementado no backend e o app
              aprovado pela Meta.
            </p>
            <div className="mt-4">
              <Button variant="outline" disabled data-testid="facebook-connect-button" title="Aguardando implementação da autenticação oficial da Meta">
                <ExternalLink className="h-4 w-4 mr-2" strokeWidth={1.5} /> Conectar via Meta (indisponível)
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Instagram — placeholder até integração oficial da Meta */}
      <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="instagram-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#FCE7F3" }}>
            <ExternalLink className="h-6 w-6" strokeWidth={1.5} style={{ color: "#E1306C" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-medium font-heading">Instagram</h3>
              <span className="text-xs uppercase tracking-widest font-medium px-2 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#6B7280" }}>
                Integração ainda não configurada
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              A conexão será feita via login oficial da Meta (Instagram Graph API). Nenhuma
              simulação: mensagens de DM só aparecerão quando o fluxo oficial estiver
              implementado e a conta comercial vinculada.
            </p>
            <div className="mt-4">
              <Button variant="outline" disabled data-testid="instagram-connect-button" title="Aguardando implementação da autenticação oficial da Meta">
                <ExternalLink className="h-4 w-4 mr-2" strokeWidth={1.5} /> Conectar via Meta (indisponível)
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
